import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catExpanded, setCatExpanded] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between h-16">
        <Link
          to="/"
          className="text-foreground-900 font-heading text-xl font-bold tracking-tight whitespace-nowrap"
        >
          <span className={scrolled ? 'text-foreground-900' : 'text-white'}>Hipobuy Sheet</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <a
            href="#categories"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
          >
            Categories
          </a>
          <a
            href="#guides"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
          >
            Guides
          </a>
          <Link
            to="/hipobuy-spreadsheet-comparison/"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
          >
            Comparison
          </Link>
          <Link
            to="/hipobuy-spreadsheet-safety/"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
          >
            Safety
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className={`whitespace-nowrap text-sm font-semibold px-5 py-2 rounded-full border transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
                : 'border-white/70 text-white hover:bg-white/10'
            }`}
          >
            Hipobuy Sheet
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-foreground-900' : 'text-white'}`}
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-6 py-4 flex flex-col max-h-[75vh] overflow-y-auto">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold mb-3"
          >
            <i className="ri-shopping-bag-line"></i>
            Hipobuy Sheet
          </a>

          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-home-4-line text-foreground-400"></i>
            Home
          </Link>

          <button
            onClick={() => setCatExpanded(!catExpanded)}
            className="flex items-center justify-between w-full px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-foreground-400"></i>
              Product Categories
            </span>
            {catExpanded ? <i className="ri-arrow-up-s-line text-foreground-400 text-sm"></i> : <i className="ri-arrow-down-s-line text-foreground-400 text-sm"></i>}
          </button>

          {catExpanded && (
            <div className="ml-4 pl-4 border-l-2 border-background-200 space-y-0.5 mb-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 text-foreground-600 text-xs font-medium rounded-lg hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150 truncate"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <div className="my-1 border-t border-background-200"></div>

          <span className="px-3 py-2 text-foreground-400 text-xs font-semibold uppercase tracking-wider">
            SEO Guides
          </span>

          <Link
            to="/hipobuy-spreadsheet/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-guide-line text-foreground-400"></i>
            Hipobuy Spreadsheet
          </Link>

          <Link
            to="/hipobuy-spreadsheet-comparison/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-scales-3-line text-foreground-400"></i>
            Complete Comparison
          </Link>

          <Link
            to="/hipobuy-spreadsheet-safety/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-shield-check-line text-foreground-400"></i>
            Safety Guide
          </Link>

          <Link
            to="/hipobuy-spreadsheet-categories/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-price-tag-3-line text-foreground-400"></i>
            Categories Guide
          </Link>

          <div className="my-1 border-t border-background-200"></div>

          <span className="px-3 py-2 text-foreground-400 text-xs font-semibold uppercase tracking-wider">
            Competitor Guides
          </span>

          <Link
            to="/kakobuy-spreadsheet/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-article-line text-foreground-400"></i>
            Kakobuy Spreadsheet
          </Link>

          <Link
            to="/cssbuy-spreadsheet/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-article-line text-foreground-400"></i>
            CSSBuy Spreadsheet
          </Link>

          <Link
            to="/superbuy-spreadsheet/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-article-line text-foreground-400"></i>
            Superbuy Spreadsheet
          </Link>

          <Link
            to="/oopbuy-spreadsheet/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-article-line text-foreground-400"></i>
            Oopbuy Spreadsheet
          </Link>

          <div className="my-1 border-t border-background-200"></div>

          <a
            href="#categories"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-apps-2-line text-foreground-400"></i>
            Category Grid
          </a>
          <a
            href="#faq"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-3 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-question-answer-line text-foreground-400"></i>
            FAQ
          </a>
        </div>
      )}
    </nav>
  );
}